import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { WorkflowDetailsCard, SummarizationCard, QualityChecksCard, DocumentMatchingCard } from '@/components';
import type { ContentNode, QualityCheckNode, DocumentMetadata } from '@/types/workflow';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createWorkflow } from '@/store/slices/workflowsSlice';



export default function WorkflowCreateView() {
  const [title, setTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [titleError, setTitleError] = useState('');
  const [tagsError, setTagsError] = useState('');
  const [contentNodes, setContentNodes] = useState<ContentNode[]>([]);
  const [qualityCheckNodes, setQualityCheckNodes] = useState<QualityCheckNode[]>([]);
  const [documentMatches, setDocumentMatches] = useState<DocumentMetadata[]>([]);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: availableTags } = useAppSelector((state) => state.tags) as {
    items: Array<{ id: number; name: string }>;
  };
  const { loading: workflowLoading } = useAppSelector((state) => state.workflows) as {
    loading: boolean;
  };

  const handleSaveWorkflow = async () => {
    // Reset previous errors
    setTitleError('');
    setTagsError('');

    // Validate title
    if (!title.trim()) {
      setTitleError('Workflow Title is required');
    }

    // Validate tags
    if (selectedTags.length === 0) {
      setTagsError('At least one tag is required');
    }

    // Validate content nodes
    const validContentNodes = contentNodes.filter(node => node.prompt && node.prompt.trim());
    if (validContentNodes.length === 0) {
      return;
    }

    // If there are validation errors, don't proceed
    if (!title.trim() || selectedTags.length === 0) {
      return;
    }

    try {
      // Convert selected tag names to tag IDs
      const tagIds = selectedTags
        .map(tagName => {
          const tag = availableTags.find(t => t.name === tagName);
          return tag?.id;
        })
        .filter((id): id is number => id !== undefined);

      // Convert content nodes to workflow nodes
      const contentWorkflowNodes = validContentNodes.map((node, index) => ({
        node_type: 'CONTENT' as const,
        prompt: node.prompt.trim(),
        sequence_order: index + 1
      }));

      // Convert quality check nodes to workflow nodes
      const validQualityCheckNodes = qualityCheckNodes.filter(node => node.prompt && node.prompt.trim());
      const qualityCheckWorkflowNodes = validQualityCheckNodes.map((node) => ({
        node_type: 'QC' as const,
        prompt: node.prompt.trim(),
        pass_fail_prompt: node.passFailPrompt.trim() || undefined,
        sequence_order: undefined // QC nodes don't have sequence order as they run in parallel
      }));

      // Combine all workflow nodes
      const workflowNodes = [...contentWorkflowNodes, ...qualityCheckWorkflowNodes];

      // Convert document metadata to API format
      const documentMetadata = documentMatches.map(match => ({
        proForma: match.documentType,
        documentType: match.category,
        territory: match.status
      }));

      // Create workflow data
      const workflowData = {
        title: title.trim(),
        tag_ids: tagIds,
        workflow_nodes: workflowNodes,
        document_metadata: documentMetadata
      };

      // Dispatch the create workflow action
      const result = await dispatch(createWorkflow(workflowData)).unwrap();
      
      // Show success message and navigate back to workflows list
      console.log('Workflow created successfully:', result);
      alert('Workflow created successfully!');
      
      // Navigate to workflows list
      navigate('/workflows/');
      
    } catch (error) {
      console.error('Failed to create workflow:', error);
      setTitleError('Failed to create workflow. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      backgroundColor: 'background.default',
      minHeight: '100vh',
      padding: 3
    }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/workflows')}
          sx={{ 
            textDecoration: 'none', 
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Workflows
        </Link>
        <Typography color="text.primary">Create Workflow</Typography>
      </Breadcrumbs>

      {/* Header with Cancel and Save Workflow Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2,
        marginBottom: 3 
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/workflows')}
          disabled={workflowLoading}
          sx={{
            minWidth: 120
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveWorkflow}
          disabled={workflowLoading}
          sx={{
            minWidth: 120
          }}
        >
          {workflowLoading ? 'Saving...' : 'Save Workflow'}
        </Button>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid size={4}>
          <WorkflowDetailsCard 
            title={title}
            setTitle={setTitle}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            titleError={titleError}
            tagsError={tagsError}
          />
        </Grid>
        <Grid size={8}>
          <Stack spacing={2}>
            <SummarizationCard 
              contentNodes={contentNodes}
              setContentNodes={setContentNodes}
            />
            <QualityChecksCard 
              qualityCheckNodes={qualityCheckNodes}
              setQualityCheckNodes={setQualityCheckNodes}
            />
            <DocumentMatchingCard 
              documentMatches={documentMatches}
              onDocumentMatchesChange={setDocumentMatches}
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
